import { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Drawer,
  TextLink,
  TextLinkStyle,
  useTranslation,
} from '@nextgen-web-framework/all';
import { PromotionCard } from '@commerce-product-webcomponents/ui';
import { DerivedAvailabilityStatus } from '@commerce-product-webcomponents/sdk';
import { PromotionalItemOptions, SelectedProducts } from '../types/propTypes';
import styles from '../OffersDrawerNew/OffersDrawer.module.scss';
import { OffersDrawerProps, QuantityChangeFnParams } from '../OffersDrawerNew/types/propTypes';

const { OutOfStock, Nla, Nya } = DerivedAvailabilityStatus;

export const getBenefitRowKey = (id: string | undefined, isGwp: boolean | undefined) =>
  `${id}-${isGwp ? 'gwp' : 'pwp'}`;

const OffersDrawer = ({
  selectedProducts,
  itemOptions,
  promotionsNameMap,
  shouldValidateSelection,
  skipDefaultGwpSelection = false,
  minRequiredSelection = 1,
  drawerHeaderLabel,
  saveCtaLabel,
  sectionTitleLabel,
  sectionInfoLabel,
  onCloseDrawer,
  onSave,
}: OffersDrawerProps) => {
  const [isSaveBtnEnable, setIsSaveBtnEnable] = useState(true);
  const [selectedProductsCopy, setSelectedProductsCopy] = useState<SelectedProducts>(selectedProducts);
  const t = useTranslation();

  useLayoutEffect(() => {
    const idMap: Record<string, string> = {};
    const _selectedProducts = { ...selectedProducts };
    itemOptions.forEach((item) => {
      const benefitRowKey = getBenefitRowKey(item.id, item.giftPromotion);
      if (!skipDefaultGwpSelection && item.giftPromotion && !selectedProducts[benefitRowKey]) {
        Object.assign(_selectedProducts, preSelectGwpProduct(item));
      }

      item.promotionalItems?.forEach((promoItem, index) => {
        const data = promoItem.itemSet?.reduce(
          (res, cur) => ({ ...res, [cur.id]: getSectionId(item.prmId, index) }),
          {},
        );
        Object.assign(idMap, data);
      });

      // Add sectionId to selected products
      Object.keys(_selectedProducts).forEach((id) => {
        const obj = Object.keys(_selectedProducts[id]).reduce(
          (res, productId) => ({
            ...res,
            [productId]: { ..._selectedProducts[id][productId], sectionId: idMap[productId] },
          }),
          {},
        );
        _selectedProducts[id] = { ...obj };
      });
    });
    setSelectedProductsCopy(_selectedProducts);
  }, []);

  useEffect(() => {
    if (shouldValidateSelection) {
      setIsSaveBtnEnable(validate(selectedProductsCopy, selectedProducts));
    }
  }, [selectedProductsCopy]);

  const getSectionId = (prmId: string | undefined, rowIndex: number) => `prmId:${prmId}-set:${rowIndex}`;

  const preSelectGwpProduct = (itemOption: PromotionalItemOptions): SelectedProducts | null => {
    const { id = '', prmId, giftPromotion, promotionalItems } = itemOption;
    const { itemSet = [], selectableItemQuantity = 0 } = withDefaultObject(promotionalItems?.[0]);
    const benefitRowKey = getBenefitRowKey(id, giftPromotion);
    for (const product of itemSet) {
      if (!([OutOfStock, Nla, Nya] as string[]).includes(String(product.derivedAvailabilityStatus))) {
        return {
          [benefitRowKey]: {
            [product.id]: {
              id,
              prmId,
              giftPromotion,
              quantity: selectableItemQuantity,
              sectionId: getSectionId(prmId, 0),
            },
          },
        };
      }
    }
    return null;
  };

  const validate = (state1: SelectedProducts, state2: SelectedProducts): boolean => {
    if (Object.keys(state1).length !== Object.keys(state2).length) {
      return true;
    }
    for (const id of Object.keys(state1)) {
      if (!state2[id] || Object.keys(state1[id]).length !== Object.keys(state2[id]).length) {
        return true;
      }
      let totalSelectedQty = 0;
      let sectionModified = false;
      for (const productId of Object.keys(state1[id])) {
        const { modified, quantity } = state1[id][productId];
        totalSelectedQty += quantity;
        sectionModified = sectionModified || Boolean(modified);
        if (sectionModified && totalSelectedQty >= minRequiredSelection) {
          return true;
        }
      }
    }

    return false;
  };

  const handleQuantityChange = ({
    id = '',
    prmId,
    itemId,
    giftPromotion = false,
    quantity,
    sectionId,
  }: QuantityChangeFnParams) => {
    setSelectedProductsCopy((prev) => {
      const benefitRowKey = getBenefitRowKey(id, giftPromotion);
      const newState = { ...prev, [benefitRowKey]: { ...withDefaultObject(prev[benefitRowKey]) } };
      const existingProduct = selectedProducts[benefitRowKey]?.[itemId];
      if (quantity === 0 && !existingProduct) {
        delete newState[benefitRowKey][itemId];
        if (Object.keys(newState[benefitRowKey]).length === 0) {
          delete newState[benefitRowKey];
        }
      } else {
        const selectedProduct = withDefaultObject(newState[benefitRowKey][itemId]);
        newState[benefitRowKey][itemId] = {
          ...selectedProduct,
          ...(existingProduct ? { modified: quantity !== existingProduct.quantity } : {}),
          id,
          prmId,
          giftPromotion,
          quantity,
          sectionId,
        };
      }
      return newState;
    });
  };

  const handleSaveClick = () => {
    setIsSaveBtnEnable(false);
    const onSuccess = () => {
      onCloseDrawer();
      setIsSaveBtnEnable(true);
    };
    const onError = () => setIsSaveBtnEnable(true);
    onSave?.(selectedProductsCopy, onSuccess, onError);
  };

  const getSelectedProductsQty = (id = '', sectionId?: string) =>
    Object.values(withDefaultObject(selectedProductsCopy[id])).reduce((acc, cur) => {
      if (sectionId !== undefined) {
        return acc + withTernaryOperator(cur.sectionId == sectionId, cur.quantity, 0);
      }
      return acc + cur.quantity;
    }, 0);

  const withDefaultNumber = (value?: number) => value ?? 0;
  const withDefaultObject = <T extends object>(value?: T) => value ?? ({} as T);
  const withDefaultString = (value?: string) => value ?? '';
  const withTernaryOperator = <T1, T2>(flag: boolean | undefined, value1: T1, value2: T2) => (flag ? value1 : value2);

  return (
    <div className={styles['offers-drawer-container']}>
      <Drawer
        header={drawerHeaderLabel ?? t('promotion.specialOffer.drawerHeading')}
        closeOnBackdropClick
        closeIcon
        bottomGap={0}
        onClose={onCloseDrawer}
        footer={
          <div className={styles['drawer-footer']}>
            <Button
              data-testid="sop-save-btn"
              title={saveCtaLabel ?? t('promotion.specialOffer.confirmButton')}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              onClick={handleSaveClick}
              disabled={!isSaveBtnEnable}
            />
            {/* Show Skip CTA only when shouldValidateSelection = false, and not undefined */}
            {shouldValidateSelection === false && (
              <TextLink
                data-testid="sop-skip-btn"
                onClick={onCloseDrawer}
                text={t('promotion.specialOffer.skip')}
                textLinkStyle={TextLinkStyle.UNDERLINE}
              />
            )}
          </div>
        }
      >
        <div className={styles['drawer-content']}>
          {itemOptions?.map((option, i) => {
            const benefitRowKey = getBenefitRowKey(option.id, option.giftPromotion);
            const totalSelectedProductQty = getSelectedProductsQty(benefitRowKey);
            const totalSelectableItemQuantity = withDefaultNumber(option.totalSelectableQuantity);
            const totalRemainingQty = totalSelectableItemQuantity - totalSelectedProductQty;
            const promotionalSetsCount = Number(option.promotionalItems?.length);
            const promoTitle = promotionsNameMap[`${option.prmId}`];

            return (
              <Fragment key={benefitRowKey}>
                {i > 0 && <hr />}
                <section className={styles['promotion-section']}>
                  {(promoTitle || promotionalSetsCount > 1) && (
                    <div className={styles['promotion-section-header']}>
                      {promotionsNameMap[`${option.prmId}`] || t('promotion.specialOffer.chooseProducts')}
                      &nbsp;
                      {promotionalSetsCount > 1 && `(${totalSelectedProductQty}/${totalSelectableItemQuantity})`}
                    </div>
                  )}
                  <div>
                    {option.promotionalItems?.map((promoItem, promotionalItemIndex) => {
                      const sectionId = getSectionId(option.prmId, promotionalItemIndex);
                      const selectedProductQty = getSelectedProductsQty(benefitRowKey, sectionId);
                      const { selectableItemQuantity = 0, amount = 0, itemSet = [] } = withDefaultObject(promoItem);
                      const maxSelectableQty = Math.min(selectedProductQty + totalRemainingQty, selectableItemQuantity);

                      return (
                        <section className={styles['promotional-item-section']} key={sectionId}>
                          <div className={styles['section-header']}>
                            <div
                              className={`${styles['section-title']} ${maxSelectableQty == 0 && styles['disabled']}`}
                            >
                              <span>{`(${selectedProductQty}/${selectableItemQuantity})`}</span>
                              &nbsp;
                              <span>{sectionTitleLabel ?? t('promotion.specialOffer.drawerProductsSelcted')}</span>
                            </div>
                            {sectionInfoLabel && <span className={styles['section-info']}>{sectionInfoLabel}</span>}
                          </div>
                          <ul className={styles['product-lists']}>
                            {itemSet?.map((item) => {
                              const remainingQty = maxSelectableQty - selectedProductQty;
                              const isOutOfStock = item.derivedAvailabilityStatus === OutOfStock;
                              const currentQty = withDefaultNumber(
                                selectedProductsCopy[withDefaultString(benefitRowKey)]?.[item.id]?.quantity,
                              );
                              const maxCount = withTernaryOperator(isOutOfStock, currentQty, currentQty + remainingQty);

                              const onQuantityChange = (quantity: number) =>
                                handleQuantityChange({
                                  id: option.id,
                                  prmId: option.prmId,
                                  itemId: item.id,
                                  giftPromotion: option.giftPromotion,
                                  quantity,
                                  sectionId,
                                });

                              return (
                                <li key={item.id}>
                                  <PromotionCard
                                    productId={item.id}
                                    productName={withDefaultString(item.name)}
                                    brandName={withDefaultString(item.brand)}
                                    link={withDefaultString(item.cartIconPublicUrl)}
                                    alt={withDefaultString(item.cartIconAltText)}
                                    derivedAvailabilityStatus={item.derivedAvailabilityStatus}
                                    productSize={(!option.giftPromotion && item.copyServingSize) || ''}
                                    productPrice={withTernaryOperator(option.giftPromotion, 0, amount)}
                                    showZeroAsFree
                                    currency={item.basePrice?.currency}
                                    maxCount={maxCount}
                                    productCount={currentQty}
                                    disabled={
                                      (isOutOfStock && currentQty === 0) ||
                                      (selectedProductQty === maxSelectableQty && currentQty === 0)
                                    }
                                    strikethroughPrice={withTernaryOperator(
                                      !option.giftPromotion,
                                      item.basePrice?.value,
                                      0,
                                    )}
                                    onQuantityIncrease={onQuantityChange}
                                    onQuantityDecrease={onQuantityChange}
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        </section>
                      );
                    })}
                  </div>
                </section>
              </Fragment>
            );
          })}
        </div>
      </Drawer>
    </div>
  );
};

export default OffersDrawer;
